import React, { useState } from "react";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * UniversalSearch — foundation only.
 *
 * Rules:
 * - Clients MUST NOT compute search results locally. Every provider is a
 *   thin wrapper around a backend endpoint (e.g. /api/v1/parties/search).
 * - A `provider` is: { id, label, fetch(query) → Promise<Array<{id,label,hint?}>> }.
 * - Modules register providers at module boot; this component just dispatches.
 *
 * Sprint 10 ships the architecture. Individual provider implementations
 * (products, parties, orders, documents) land with their module in later
 * sprints.
 */
export function UniversalSearch({ providers = [], open, onOpenChange, testid = "phx-universal-search" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({});

  const onQueryChange = async (value) => {
    setQuery(value);
    if (!value || value.length < 2) {
      setResults({});
      return;
    }
    const next = {};
    await Promise.all(
      providers.map(async (p) => {
        try {
          next[p.id] = await p.fetch(value);
        } catch {
          next[p.id] = [];
        }
      })
    );
    setResults(next);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <Command data-testid={testid}>
        <CommandInput
          data-testid={`${testid}-input`}
          placeholder="Search everything…"
          value={query}
          onValueChange={onQueryChange}
        />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          {providers.map((p) => (
            <CommandGroup key={p.id} heading={p.label}>
              {(results[p.id] || []).map((item) => (
                <CommandItem key={`${p.id}:${item.id}`} data-testid={`${testid}-item-${p.id}-${item.id}`}>
                  <span className="font-medium">{item.label}</span>
                  {item.hint && (
                    <span className="ml-auto text-xs text-[var(--phx-slate-500)]">{item.hint}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
